import { useEffect, useRef, useCallback } from 'react';

type LogoutFunction = () => void;

const useInactivityTimeout = (
  onLogout: LogoutFunction,
  inactivityTime: number = 30 * 60 * 1000 // Default to 30 minutes
): void => {
  const lastActivityTimeRef = useRef<number>(Date.now());

  // Memoize to ensure stable reference
  const handleActivity = useCallback(() => {
    lastActivityTimeRef.current = Date.now();
  }, []);

  useEffect(() => {
    const checkInactivity = () => {
      const tokenExpiry = localStorage.getItem('user-token-expiry-date');
      const isTokenExpired = tokenExpiry ? new Date() > new Date(tokenExpiry) : false;

      if ((Date.now() - lastActivityTimeRef.current) > inactivityTime || isTokenExpired) {
        onLogout();
      } else {
        const newExpiryDate = new Date();
        newExpiryDate.setMinutes(newExpiryDate.getMinutes() + 30);
        localStorage.setItem('user-token-expiry-date', newExpiryDate.toISOString());
      }
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('scroll', handleActivity);

    const intervalId = setInterval(checkInactivity, 1000 * 30);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity);
    };
  }, [inactivityTime, onLogout, handleActivity]);
};

export default useInactivityTimeout;
